import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { authService } from './auth';
import { SOVFile, Property, Parser, Pipeline, PropertyImage, AssignmentPoolMember, PaginatedSOVFilesResponse, SOVFilesQueryParams } from '../types';
import { ImageFetchConfig } from '../types/imageFetch';
import { performanceMonitor } from './performanceMonitor';
import { toast } from '../components/ui/Toast';

class ApiService {
  public api: AxiosInstance;
  private currentOrganizationId: number | null = null;

  private trackAPIPerformance(config: any, response?: AxiosResponse, error?: any) {
    const startTime = config.metadata?.startTime;
    if (!startTime) return;

    const endTime = performance.now();
    const url = config.url || '';
    const method = (config.method || 'GET').toUpperCase();
    const status = response?.status || error?.response?.status || 0;
    const size = response?.headers?.['content-length'] ? parseInt(response.headers['content-length']) : undefined;

    performanceMonitor.trackAPICall(url, method, startTime, endTime, status, size);
  }

  constructor() {
    const getApiUrl = () => {
      // First check if there's an environment variable set
      if (import.meta.env.VITE_API_URL !== undefined) {
        // If empty string, use relative URLs (same domain)
        return import.meta.env.VITE_API_URL || '';
      }
      
      // Detect the current host and construct API URL dynamically
      const host = window.location.hostname;
      const protocol = window.location.protocol;
      
      // For localhost/127.0.0.1, use localhost:8001
      if (host === 'localhost' || host === '127.0.0.1') {
        return 'http://localhost:8001';
      }
      
      // For Cloudflare/production domains, use relative URLs
      if (host.includes('umbrellavision.com') || protocol === 'https:') {
        return ''; // Empty string means use relative URLs
      }
      
      // For any other host (including IP addresses), use the same host with port 8001
      // This handles both local network IPs (192.168.x.x) and public IPs
      return `${protocol}//${host}:8001`;
    };
    
    const baseURL = getApiUrl();
    console.log('API Service initialized with baseURL:', baseURL || 'relative URLs');
    
    this.api = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 second timeout
    });

    // Add auth token and organization context to requests
    this.api.interceptors.request.use(async (config) => {
      // Use async token validation to ensure fresh tokens
      const authHeader = await authService.getAuthHeaderAsync();
      Object.assign(config.headers, authHeader);
      
      // Add organization context header
      if (this.currentOrganizationId) {
        config.headers['X-Organization-ID'] = this.currentOrganizationId.toString();
      }
      
      // Add performance tracking timestamp
      (config as any).metadata = { startTime: performance.now() };
      
      return config;
    });

    // Handle auth errors and performance tracking
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Track API performance
        this.trackAPIPerformance(response.config, response);
        return response;
      },
      async (error) => {
        // Track failed API performance
        if (error.config) {
          this.trackAPIPerformance(error.config, error.response, error);
        }
        
        // Log error details for debugging
        console.error('API Error:', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,  // Include server error details
          code: error.code,
          message: error.message,
          baseURL: error.config?.baseURL,
        });
        
        if (error.response?.status === 401) {
          // Check if this is already a retry attempt
          if (!error.config._retry) {
            error.config._retry = true;
            
            try {
              // Attempt to refresh the token
              console.log('Token expired, attempting refresh...');
              toast.info('Session Expiring', 'Refreshing your authentication...');
              const newToken = await authService.refreshAccessToken();
              
              if (newToken) {
                // Update the failed request with new token
                const authHeader = await authService.getAuthHeaderAsync();
                Object.assign(error.config.headers, authHeader);
                
                // Retry the original request
                return this.api.request(error.config);
              }
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              toast.error('Session Expired', 'Please log in again to continue.');
            }
          }
          
          // Only redirect to login if not already on login page and refresh failed
          if (!window.location.pathname.includes('/login')) {
            // Clear all tokens
            authService.clearTokens();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Organization context management
  setOrganizationId(organizationId: number): void {
    this.currentOrganizationId = organizationId;
  }

  // SOV File endpoints
  async uploadSOVFile(file: File): Promise<{ file_id: string; status: string }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.api.post('/api/sov/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async parseSOVFile(fileId: string, parserIdOrPipelineId: string, enrichmentConfig?: {
    construction_scheme?: string;
    enable_google_search?: boolean;
    fetch_images?: boolean;
    image_fetch_config?: any;
    use_pipeline?: boolean;
  }) {
    const requestData: any = {
      ...enrichmentConfig,
    };
    
    // Check if using pipeline
    if (enrichmentConfig?.use_pipeline || parserIdOrPipelineId.includes('_')) {
      requestData.pipeline_id = parserIdOrPipelineId;
    } else {
      requestData.parser_name = parserIdOrPipelineId;
    }
    
    const response = await this.api.post(`/api/sov/parse/${fileId}`, requestData);
    return response.data;
  }

  async getSOVFiles(params?: SOVFilesQueryParams): Promise<PaginatedSOVFilesResponse> {
    const response = await this.api.get('/api/sov/files/optimized', { params });
    return response.data;
  }

  async getSOVFile(fileId: string) {
    const response = await this.api.get(`/api/sov/${fileId}`);
    return response.data;
  }

  async getSOVComparison(fileId: string, params?: { limit?: number; offset?: number }) {
    const response = await this.api.get(`/api/sov/${fileId}/comparison`, { params });
    return response.data;
  }

  async getPropertyDetails(propertyId: string) {
    const response = await this.api.get(`/api/sov/property/${propertyId}/details`);
    return response.data;
  }

  async getOriginalPropertyValues(propertyId: string) {
    const response = await this.api.get(`/api/sov/property/${propertyId}/original`);
    return response.data;
  }

  async updateProperty(propertyId: string, data: Partial<Property>, editReason?: string) {
    const response = await this.api.put(`/api/sov/property/${propertyId}`, {
      ...data,
      edit_reason: editReason,
    });
    return response.data;
  }

  async verifyProperty(propertyId: string, verified: boolean) {
    const response = await this.api.put(`/api/sov/property/${propertyId}/verify`, {
      verified,
    });
    return response.data;
  }

  async createProperty(fileId: string, propertyData: Partial<Property>) {
    const response = await this.api.post(`/api/sov/${fileId}/property`, propertyData);
    return response.data;
  }

  async archiveProperty(propertyId: string, reason?: string) {
    const response = await this.api.patch(`/api/sov/property/${propertyId}/archive`, {
      reason: reason || 'Deleted by user',
    });
    return response.data;
  }

  async getEnrichmentCodes(codeType: 'construction' | 'occupancy') {
    const response = await this.api.get(`/api/sov/codes/${codeType}`);
    return response.data;
  }

  async exportSOV(fileId: string, format: 'csv' | 'xlsx' = 'csv') {
    const response = await this.api.post(`/api/sov/${fileId}/export`, null, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  }

  async exportOriginal(fileId: string): Promise<{ blob: Blob; filename?: string }> {
    const response = await this.api.get(`/api/sov/${fileId}/export/original`, {
      responseType: 'blob',
    });
    
    // Extract filename from Content-Disposition header
    let filename: string | undefined;
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    return {
      blob: response.data,
      filename
    };
  }

  async exportParsed(fileId: string, format: 'csv' | 'xlsx' = 'csv'): Promise<Blob> {
    const response = await this.api.get(`/api/sov/${fileId}/export/parsed`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  }

  async exportVerified(fileId: string, format: 'csv' | 'xlsx' = 'csv', verifiedOnly: boolean = false): Promise<Blob> {
    const response = await this.api.post(`/api/sov/${fileId}/export/verified`, null, {
      params: { format, verified_only: verifiedOnly },
      responseType: 'blob',
    });
    return response.data;
  }

  async exportEditTemplate(fileId: string): Promise<{ blob: Blob; filename?: string }> {
    const response = await this.api.get(`/api/sov/${fileId}/export/edit-template`, {
      responseType: 'blob',
    });

    // Extract filename from Content-Disposition header
    let filename: string | undefined;
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    return {
      blob: response.data,
      filename
    };
  }

  async previewImportEdits(fileId: string, file: File): Promise<{
    total_rows: number;
    matched_properties: number;
    new_properties: number;
    properties_to_update: number;
    properties_to_archive: number;
    validation_errors: Array<{
      row: number;
      field: string;
      value: string;
      error: string;
    }>;
    changes_preview: Array<{
      row: number | null;
      property_id: number | null;
      action: 'update' | 'create' | 'archive';
      loc_name?: string;
      address?: string;
      changes?: Array<{ field: string; old_value: any; new_value: any; is_clearing?: boolean }>;
    }>;
    has_more_changes: boolean;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.api.post(`/api/sov/${fileId}/import-edits/preview`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async applyImportEdits(fileId: string, file: File, createNewProperties: boolean = true): Promise<{
    success: boolean;
    updated_count: number;
    created_count: number;
    archived_count: number;
    skipped_count: number;
    errors: string[];
    has_more_errors: boolean;
    enrichment_run: boolean;
    enrichment_count: number;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.api.post(`/api/sov/${fileId}/import-edits/apply`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: { create_new_properties: createNewProperties },
    });
    return response.data;
  }

  async submitSOVFile(fileId: string, data: { selected_property_ids: string[]; submission_notes?: string }) {
    const response = await this.api.post(`/api/sov/${fileId}/submit`, data);
    return response.data;
  }

  async getSOVSubmissions(fileId: string) {
    const response = await this.api.get(`/api/sov/${fileId}/submissions`);
    return response.data;
  }

  // Parser endpoints (legacy)
  async getParsers(): Promise<{ success: boolean; parsers: Parser[] }> {
    const response = await this.api.get('/api/sov/parsers');
    return response.data;
  }

  // Pipeline endpoints
  async getPipelines(status?: 'production' | 'development', active_only: boolean = true): Promise<Pipeline[]> {
    const params: any = { active_only };
    if (status) {
      params.status = status;
    }
    const response = await this.api.get('/api/pipelines', { params });
    return response.data;
  }

  async getPipeline(pipelineId: string): Promise<Pipeline> {
    const response = await this.api.get(`/api/pipelines/${pipelineId}`);
    return response.data;
  }

  async createPipeline(pipeline: Omit<Pipeline, 'created_at' | 'updated_at' | 'version_string' | 'display_name'>): Promise<Pipeline> {
    const response = await this.api.post('/api/pipelines', pipeline);
    return response.data;
  }

  async updatePipelineStatus(pipelineId: string, status: 'production' | 'development'): Promise<{ message: string }> {
    const response = await this.api.put(`/api/pipelines/${pipelineId}/status`, null, {
      params: { status }
    });
    return response.data;
  }

  async getSourcePipelines(): Promise<Pipeline[]> {
    const response = await this.api.get('/api/pipelines/sources');
    return response.data;
  }

  async createNewPipeline(data: {
    source_pipeline_id: string;
    name: string;
    description?: string;
  }): Promise<Pipeline> {
    const response = await this.api.post('/api/pipelines/create', data);
    return response.data;
  }

  // Validation endpoints
  async createValidation(data: {
    document_id: string;
    document_name: string;
    property_id?: string;
    property_address: string;
    pipeline_id?: string;
    pipeline_version?: any;
    validated_fields: Record<string, any>;
    validated_by?: string;
    notes?: string;
  }) {
    const response = await this.api.post('/api/validations', data);
    return response.data;
  }

  async getDocumentValidations(documentId: string) {
    const response = await this.api.get(`/api/validations/document/${documentId}`);
    return response.data;
  }

  async getPipelineValidations(pipelineId: string) {
    const response = await this.api.get(`/api/validations/pipeline/${pipelineId}`);
    return response.data;
  }

  async getValidationStats() {
    const response = await this.api.get('/api/validations/stats');
    return response.data;
  }

  // Failure report endpoints
  async createFailureReport(data: {
    document_id: string;
    property_id?: string;
    comment: string;
    failure_category?: string;
    parser_version?: string;
    failure_details?: any;
  }) {
    const response = await this.api.post('/api/failure-reports', data);
    return response.data;
  }

  async getFailureReports(params?: {
    status?: string;
    document_id?: string;
    organization_id?: number;
    skip?: number;
    limit?: number;
  }) {
    const response = await this.api.get('/api/failure-reports', { params });
    return response.data;
  }

  async getFailureReport(reportId: number) {
    const response = await this.api.get(`/api/failure-reports/${reportId}`);
    return response.data;
  }

  async updateFailureReport(reportId: number, data: {
    status?: string;
    resolution_notes?: string;
    comment?: string;
  }) {
    const response = await this.api.patch(`/api/failure-reports/${reportId}`, data);
    return response.data;
  }

  async deleteFailureReport(reportId: number) {
    const response = await this.api.delete(`/api/failure-reports/${reportId}`);
    return response.data;
  }

  // Image endpoints
  async getPropertyImages(params?: {
    skip?: number;
    limit?: number;
    image_type?: string;
    has_marked_version?: boolean;
  }): Promise<{ images: PropertyImage[]; total: number }> {
    const response = await this.api.get('/api/sov/images/dual-version', { params });
    
    // Get the base URL for constructing absolute URLs
    const baseURL = this.api.defaults.baseURL || '';
    
    // Normalize the response to match PropertyImage interface
    const normalizedImages = response.data.images.map((img: any) => {
      // Convert relative URLs to absolute URLs
      const makeAbsoluteUrl = (url: string | null) => {
        if (!url) return null;
        if (url.startsWith('http')) return url;
        return `${baseURL}${url.startsWith('/') ? '' : '/'}${url}`;
      };
      
      return {
        ...img,
        image_url: makeAbsoluteUrl(img.unmarked_url) || makeAbsoluteUrl(img.image_url),
        unmarked_url: makeAbsoluteUrl(img.unmarked_url),
        marked_url: makeAbsoluteUrl(img.marked_url),
        marked_image_url: makeAbsoluteUrl(img.marked_url) || makeAbsoluteUrl(img.marked_image_url),
        // Extract address from metadata if available
        metadata: {
          ...img.metadata,
          address: img.metadata?.address || img.address,
          city: img.metadata?.city || img.city,
          state: img.metadata?.state || img.state,
        }
      };
    });
    
    console.log(`Fetched ${normalizedImages.length} images from API`);
    
    return {
      images: normalizedImages,
      total: response.data.total,
    };
  }

  async getPropertyImagesByPropertyId(propertyId: string): Promise<PropertyImage[]> {
    const response = await this.api.get(`/api/sov/property/${propertyId}/images`);
    
    // Get the base URL for constructing absolute URLs
    const baseURL = this.api.defaults.baseURL || '';
    
    // Convert relative URLs to absolute URLs
    const makeAbsoluteUrl = (url: string | null) => {
      if (!url) return null;
      if (url.startsWith('http')) return url;
      return `${baseURL}${url.startsWith('/') ? '' : '/'}${url}`;
    };
    
    // Normalize the response
    return response.data.images?.map((img: any) => ({
      ...img,
      image_type: img.type, // Map 'type' to 'image_type' for consistency
      image_url: makeAbsoluteUrl(img.unmarked_url) || makeAbsoluteUrl(img.url) || makeAbsoluteUrl(img.image_url),
      unmarked_url: makeAbsoluteUrl(img.unmarked_url) || makeAbsoluteUrl(img.url),
      marked_url: makeAbsoluteUrl(img.marked_url),
      marked_image_url: makeAbsoluteUrl(img.marked_url) || makeAbsoluteUrl(img.marked_image_url),
      has_marked_version: img.has_marked_version || !!img.marked_url,
      view_angle: img.view_angle,
      zoom_level: img.zoom_level,
      confidence: img.confidence,
      google_maps_street_url: img.google_maps_street_url,
      google_maps_aerial_url: img.google_maps_aerial_url,
    })) || [];
  }

  async fetchPropertyImages(propertyId: string, fileId: string) {
    const response = await this.api.post(`/api/sov/property/${propertyId}/fetch-images`, {
      file_id: fileId,
    });
    return response.data;
  }

  async fetchBulkPropertyImages(fileId: string, propertyIds?: number[]) {
    try {
      const payload = {
        property_ids: propertyIds || null  // null means fetch for all properties without images
      };
      console.log('Sending bulk image fetch request:', { fileId, payload });
      const response = await this.api.post(`/api/sov/${fileId}/fetch-images-bulk`, payload);
      return response.data;
    } catch (error: any) {
      console.error('Bulk image fetch error details:', {
        status: error.response?.status,
        data: error.response?.data,
        headers: error.response?.headers
      });
      throw error;
    }
  }

  async getImageFetchStatus(fileId: string, taskId: string) {
    const response = await this.api.get(`/api/sov/${fileId}/fetch-images-status/${taskId}`);
    return response.data;
  }


  // Organization endpoints
  async getOrganizations() {
    const response = await this.api.get('/api/organizations');
    return response.data;
  }


  async createOrganization(data: { name: string; description?: string }) {
    const response = await this.api.post('/api/organizations', data);
    return response.data;
  }


  async deleteOrganization(orgId: number, confirmation: string) {
    const formData = new FormData();
    formData.append('confirmation', confirmation);
    
    const response = await this.api.delete(`/api/organizations/${orgId}`, {
      data: formData,
    });
    return response.data;
  }

  async deactivateOrganization(orgId: number, organizationName: string) {
    const response = await this.api.post(`/api/organizations/${orgId}/deactivate`, {
      organization_name: organizationName
    });
    return response.data;
  }

  // Invitation endpoints
  async getMyInvitations() {
    const response = await this.api.get('/api/invitations/my-invitations');
    return response.data;
  }

  async declineInvitation(token: string, reason?: string) {
    const response = await this.api.put(`/api/invitations/${token}/decline`, { reason });
    return response.data;
  }

  // Organization context methods
  setOrganizationContext(organizationId: number) {
    this.currentOrganizationId = organizationId;
  }

  getOrganizationContext(): number | null {
    return this.currentOrganizationId;
  }

  // Get user's organizations
  async getUserOrganizations() {
    const response = await this.api.get('/api/auth/user/organizations');
    return response.data.organizations || [];
  }

  // Task Queue endpoints
  async createImageFetchTask(data: {
    file_id: string;
    property_ids?: number[];
    google_maps_api_key?: string;
    config?: ImageFetchConfig;
  }) {
    const response = await this.api.post('/api/tasks/image-fetch', data);
    return response.data;
  }

  async getTaskStatus(taskId: string) {
    const response = await this.api.get(`/api/tasks/${taskId}`);
    return response.data;
  }

  async listTasks(params?: {
    status?: string;
    task_type?: string;
    limit?: number;
    offset?: number;
  }) {
    const response = await this.api.get('/api/tasks', { params });
    return response.data;
  }

  async cancelTask(taskId: string) {
    const response = await this.api.delete(`/api/tasks/${taskId}`);
    return response.data;
  }

  // Organization management
  async getOrganization(orgId: string | number) {
    const response = await this.api.get(`/api/organizations/${orgId}`);
    return response.data;
  }

  async getOrganizationMembers(orgId: string | number) {
    const response = await this.api.get(`/api/organizations/${orgId}/members`);
    return response.data;
  }

  async updateOrganization(orgId: string | number, data: any) {
    const response = await this.api.put(`/api/organizations/${orgId}`, data);
    return response.data;
  }

  async uploadOrganizationLogo(orgId: string | number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.api.post(`/api/organizations/${orgId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateMemberRole(orgId: string | number, memberId: string, data: { role: string }) {
    const response = await this.api.put(`/api/organizations/${orgId}/members/${memberId}/role`, data);
    return response.data;
  }

  async removeMemberFromOrganization(orgId: string | number, memberId: string) {
    const response = await this.api.delete(`/api/organizations/${orgId}/members/${memberId}`);
    return response.data;
  }

  // Invitation management
  async sendInvitation(data: {
    email: string;
    role: string;
    organization_id: number;
    custom_message?: string;
  }) {
    const response = await this.api.post('/api/invitations/send', data);
    return response.data;
  }

  async sendBulkInvitations(data: {
    emails: string;
    role: string;
    organization_id: number;
    custom_message?: string;
  }) {
    const response = await this.api.post('/api/invitations/send-bulk', data);
    return response.data;
  }

  // Assignment methods
  async getAssignmentPoolMembers(orgId: number, lob?: string | null): Promise<{ members: AssignmentPoolMember[]; lob: string | null }> {
    const params: Record<string, string> = {};
    if (lob) params.lob = lob;
    const response = await this.api.get(`/api/assignments/pool/${orgId}/dropdown`, { params });
    return response.data;
  }

  async assignSOVFile(sovFileId: string, assignedToId: number, notes?: string): Promise<any> {
    const response = await this.api.post(`/api/assignments/sov/${sovFileId}/assign`, {
      assigned_to_id: assignedToId,
      notes
    });
    return response.data;
  }

  async unassignSOVFile(sovFileId: string, notes?: string): Promise<any> {
    const response = await this.api.delete(`/api/assignments/sov/${sovFileId}/assign`, {
      params: { notes }
    });
    return response.data;
  }

  // Generic methods for flexibility
  async get(path: string, config?: any) {
    const response = await this.api.get(path, config);
    return response.data;
  }

  async post(path: string, data?: any, config?: any) {
    const response = await this.api.post(path, data, config);
    return response.data;
  }

  async put(path: string, data?: any, config?: any) {
    const response = await this.api.put(path, data, config);
    return response.data;
  }

  async delete(path: string, config?: any) {
    const response = await this.api.delete(path, config);
    return response.data;
  }
}

const apiServiceInstance = new ApiService();
export const apiService = apiServiceInstance;
export const api = apiServiceInstance.api;